// Report colours from the practice logo (D5), computed at render time in Phase 1 (saving + adjusting them is Phase 2).
// PURE + deterministic: the same pixels always give the same colours. White, transparent and near-grey pixels are
// ignored; the remaining pixels are binned, the bins merged into colour clusters, and:
//   primary = the darkest dominant cluster that is dark enough for text on white (contrast ≥ 4.5; a dominant colour
//             that is too light is darkened until it is), accent = the next distinct dominant cluster.
// No logo / no usable colours → the Huddledin defaults (teal + amber).

export const DEFAULT_COLOURS = { primary: '#0d6b63', accent: '#f59e0b', source: 'default' };

const hex = ([r, g, b]) => '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
export const parseHex = h => { const m = /^#?([0-9a-f]{6})$/i.exec(String(h || '')); if (!m) return null; const n = parseInt(m[1], 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const lin = c => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
export const luminance = ([r, g, b]) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
export const contrastOnWhite = c => 1.05 / (luminance(c) + 0.05);
const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
export const mix = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

// rgba: Uint8(Clamped)Array, 4 bytes per pixel. Samples a fixed grid (≤ ~40k pixels) so big logos cost the same.
export function extractBrandColours({ rgba, width, height } = {}) {
  if (!rgba || !width || !height) return { ...DEFAULT_COLOURS };
  const step = Math.max(1, Math.floor(Math.sqrt((width * height) / 40000)));
  const bins = new Map(); let total = 0;
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const i = (y * width + x) * 4;
      const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2], a = rgba[i + 3];
      if (a < 200) continue;                                   // transparent / edge anti-aliasing
      const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      if (mn > 232) continue;                                   // white / near-white
      if (mx - mn < 24) continue;                               // grey / black / white-ish (no hue to carry)
      const key = (r >> 3) << 10 | (g >> 3) << 5 | (b >> 3);
      const e = bins.get(key) || [0, 0, 0, 0];
      e[0]++; e[1] += r; e[2] += g; e[3] += b; bins.set(key, e);
      total++;
    }
  }
  if (total < 50) return { ...DEFAULT_COLOURS };
  // merge bins into clusters, most populous first (ties broken by key → deterministic)
  const sorted = [...bins.entries()].sort((p, q) => q[1][0] - p[1][0] || p[0] - q[0]);
  const clusters = [];
  for (const [, e] of sorted) {
    const c = [e[1] / e[0], e[2] / e[0], e[3] / e[0]];
    const hit = clusters.find(k => dist(k.mean, c) < 40);
    // the cluster's colour = its most populous bin (the logo's flat fill), not the average with its anti-aliased edges
    if (hit) hit.n += e[0];
    else clusters.push({ n: e[0], mean: c });
  }
  clusters.sort((p, q) => q.n - p.n);
  const dominant = clusters.filter(k => k.n / total >= 0.06);
  if (!dominant.length) return { ...DEFAULT_COLOURS };
  const readable = dominant.filter(k => contrastOnWhite(k.mean) >= 4.5).sort((p, q) => luminance(p.mean) - luminance(q.mean));
  let primary;
  if (readable.length) primary = readable[0].mean;
  else { primary = dominant[0].mean; for (let t = 0.1; contrastOnWhite(primary) < 4.5 && t < 1; t += 0.1) primary = mix(dominant[0].mean, [0, 0, 0], t); }
  const accentK = dominant.find(k => dist(k.mean, primary) >= 60) || clusters.find(k => k.n / total >= 0.02 && dist(k.mean, primary) >= 60);
  const accent = accentK ? accentK.mean : mix(primary, [255, 255, 255], 0.45);
  return { primary: hex(primary), accent: hex(accent), source: 'logo' };
}

// The full palette the page uses, derived from primary + accent (tints for the patient box, lines, list markers).
export function palette({ primary, accent } = DEFAULT_COLOURS) {
  const b = parseHex(primary) || parseHex(DEFAULT_COLOURS.primary), a = parseHex(accent) || parseHex(DEFAULT_COLOURS.accent);
  let aDark = mix(a, [0, 0, 0], 0.22); for (let t = 0.3; contrastOnWhite(aDark) < 3 && t < 0.9; t += 0.08) aDark = mix(a, [0, 0, 0], t);
  const to1 = c => c.map(v => Math.max(0, Math.min(255, v)) / 255);
  return {
    primary: to1(b), accent: to1(a), accentDark: to1(aDark),
    tint: to1(mix(b, [255, 255, 255], 0.91)), line: to1(mix(b, [255, 255, 255], 0.74)),
    ink: to1([38, 43, 53]), muted: to1([107, 115, 133]), faint: to1([138, 148, 166]),
    hex: { primary: hex(b), accent: hex(a) },
  };
}

// Phase 2 Q2: the same palette as CSS custom properties, for the editor (and later the preview), so the screen and
// the PDF use one set of colours. Lowercase hex.
export function paletteCssVars(chosen) {
  const p = palette(chosen), h = c => hex(c.map(v => v * 255));
  return {
    '--rb-primary': h(p.primary), '--rb-accent': h(p.accent), '--rb-accent-dark': h(p.accentDark),
    '--rb-tint': h(p.tint), '--rb-line': h(p.line), '--rb-ink': h(p.ink), '--rb-muted': h(p.muted),
  };
}

// The colours a report uses: the specialist's override where set (Phase 2 Q5, report_settings.brand_primary /
// brand_accent), else extracted from the logo, else the defaults. One place for the editor, the preview and the PDF.
export function resolveColours(logo, override) {
  const base = (logo && logo.rgba ? extractBrandColours(logo) : null) || { ...DEFAULT_COLOURS };
  const p = override && parseHex(override.primary) ? '#' + String(override.primary).replace('#', '').toLowerCase() : null;
  const a = override && parseHex(override.accent) ? '#' + String(override.accent).replace('#', '').toLowerCase() : null;
  if (!p && !a) return base;
  return { primary: p || base.primary, accent: a || base.accent, source: 'custom' };
}
export const brandOverride = b => ({ primary: (b && b.brand_primary) || null, accent: (b && b.brand_accent) || null });
